import type * as React from "react";
import { Command as CommandPrimitive } from "cmdk";

import { cn } from "~/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/atoms/dialog";
import { InputGroup, InputGroupAddon } from "~/atoms/input-group";
import { SearchIcon, CheckIcon } from "lucide-react";
import i18n from "~/i18n";

function Command({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive>) {
  return (
    <CommandPrimitive
      data-slot="command"
      className={cn(
        // Base: layout, sizing, shape
        "flex size-full flex-col overflow-hidden rounded-xl! p-1",
        // Default colors
        "bg-popover text-popover-foreground",
        className,
      )}
      {...props}
    />
  );
}

function CommandDialog({
  title = i18n.t(($) => $.commons.commandPalette.title),
  description = i18n.t(($) => $.commons.commandPalette.description),
  children,
  className,
  showCloseButton = false,
  ...props
}: React.ComponentProps<typeof Dialog> & {
  title?: string;
  description?: string;
  className?: string;
  showCloseButton?: boolean;
}) {
  return (
    <Dialog {...props}>
      <DialogHeader className="sr-only">
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      <DialogContent
        className={cn(
          "top-1/3 translate-y-0 overflow-hidden rounded-xl! p-0",
          className,
        )}
        showCloseButton={showCloseButton}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
}

function CommandInput({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Input>) {
  return (
    <div data-slot="command-input-wrapper" className="p-1 pb-0">
      <InputGroup
        className={cn(
          // Base: sizing, shape
          "h-8! rounded-lg! shadow-none!",
          // Default colors
          "bg-input/30 border-border",
          // Child overrides
          "*:data-[slot=input-group-addon]:pl-2!",
        )}
      >
        <CommandPrimitive.Input
          data-slot="command-input"
          className={cn(
            // Base: layout, typography
            "w-full text-sm outline-hidden",
            // Disabled
            "disabled:cursor-not-allowed disabled:opacity-50",
            className,
          )}
          {...props}
        />
        <InputGroupAddon>
          <SearchIcon className="size-4 shrink-0 opacity-50" />
        </InputGroupAddon>
      </InputGroup>
    </div>
  );
}

function CommandList({
  className,
  showScrollbar = false,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.List> & {
  showScrollbar?: boolean;
}) {
  return (
    <CommandPrimitive.List
      data-slot="command-list"
      className={cn(
        "max-h-72 scroll-py-1 overflow-x-hidden overflow-y-auto outline-none",
        !showScrollbar && "no-scrollbar",
        className,
      )}
      {...props}
    />
  );
}

function CommandEmpty({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Empty>) {
  return (
    <CommandPrimitive.Empty
      data-slot="command-empty"
      className={cn("py-6 text-center text-sm", className)}
      {...props}
    />
  );
}

function CommandGroup({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      data-slot="command-group"
      className={cn(
        // Base: layout
        "overflow-hidden p-1",
        // Default colors
        "text-foreground",
        // Heading overrides
        "**:[[cmdk-group-heading]]:text-muted-foreground **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:py-1.5 **:[[cmdk-group-heading]]:text-xs **:[[cmdk-group-heading]]:font-medium",
        className,
      )}
      {...props}
    />
  );
}

function CommandSeparator({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Separator>) {
  return (
    <CommandPrimitive.Separator
      data-slot="command-separator"
      className={cn("bg-border -mx-1 h-px", className)}
      {...props}
    />
  );
}

function CommandItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Item>) {
  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className={cn(
        // Base: layout, sizing, shape, typography, cursor
        "group/command-item relative flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none",
        // Data-selected
        "data-selected:bg-muted data-selected:text-foreground data-selected:*:[svg]:text-foreground",
        // Disabled
        "data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50",
        // Context override (inside dialog)
        "in-data-[slot=dialog-content]:rounded-lg!",
        // SVG overrides
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      {children}
      <CheckIcon className="ml-auto opacity-0 group-has-data-[slot=command-shortcut]/command-item:hidden group-data-[checked=true]/command-item:opacity-100" />
    </CommandPrimitive.Item>
  );
}

function CommandShortcut({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="command-shortcut"
      className={cn(
        // Base: layout, typography
        "ml-auto text-xs tracking-widest",
        // Default colors
        "text-muted-foreground",
        // Group data-selected
        "group-data-selected/command-item:text-foreground",
        className,
      )}
      {...props}
    />
  );
}

/** @knipignore */
export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
};
